// ExportPanel — LAB-ONLY export affordance for a completed assistant turn that carries
// an export descriptor (ai:done.exports). Renders a small preview table (the descriptor's
// capped `sample`) + a "Download CSV" button. This is the lab's own UI: SafetyQube rebuilds
// its own button against the SAME descriptor + /export endpoint + captionLines (the portable
// contract). Nothing here ships to SQ.
//
// The button POSTs the descriptor to the engine (via axios so the JWT rides), gets the CSV
// as a blob, and triggers a native browser download. The truncation line is UI-driven and
// deterministic — built from the response's X-Export-* headers (what actually downloaded),
// never an LLM turn.
//
// `readOnly` renders the PREVIEW ALONE, for a saved transcript. The descriptor kept in the
// transcript is deliberately trimmed to its render surfaces (loop.js _renderForTranscript), so
// there is nothing left to POST — and a download button on a historical turn would re-read the
// database under today's privileges and could disagree with the numbers printed beside it.

import React, { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import styles from "../sqAi.module.css";
import { dateFieldKind, formatResultDate } from "../resultDates.mjs";
import { aiAssistantApi } from "../../../services/api";

// One preview cell — mirrors the CSV serializer's coercion so the table matches the file. A DATE
// is the one exception and it is display-only: the engine's ISO value is what the CSV carries,
// so the file keeps it and only the screen gets "Aug 22, 2026" (see resultDates).
function cellText(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

const withCommas = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString() : String(n));

// Parse filename="..." from a Content-Disposition header; fall back to a safe default.
function filenameFromDisposition(disposition, fallback) {
  const m = /filename="?([^"]+)"?/.exec(disposition || "");
  return (m && m[1]) || fallback;
}

// `zone` is the reader's `userTz`: a date-and-time cell reads on that clock, the one the answer's prose is written in.
// Display only — the CSV is the engine's file and keeps the wire value.
export default function ExportPanel({ descriptor, env, tenantSchema, readOnly = false, zone }) {
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [message, setMessage] = useState("");

  if (!descriptor) return null;
  // The date decision is per COLUMN, resolved once here rather than per cell: the same key means
  // the same field for every row, and an unclassified column renders exactly as it did before.
  const columns = (Array.isArray(descriptor.columns) ? descriptor.columns : [])
    .map((c) => ({ ...c, dateKind: dateFieldKind(c.key) }));
  const sample = Array.isArray(descriptor.sample) ? descriptor.sample : [];
  // rowCount = the number of CSV ROWS the export yields (group rows for a grouped answer;
  // the record universe for a listing). The preview "of M" hint is about rows, NOT the
  // record count (they diverge for grouped answers), so prefer rowCount.
  const fullRows = Number.isFinite(Number(descriptor.rowCount)) ? Number(descriptor.rowCount)
    : Number.isFinite(Number(descriptor.totalCount)) ? Number(descriptor.totalCount) : null;
  const canDownload = status !== "loading" && !!tenantSchema;

  const onDownload = async () => {
    setStatus("loading");
    setMessage("");
    try {
      const res = await aiAssistantApi.exportData(env, tenantSchema, descriptor);
      const h = res.headers || {};
      const returned = Number(h["x-export-returned-count"]);
      const total = h["x-export-total-count"] === "" ? null : Number(h["x-export-total-count"]);
      const truncated = h["x-export-truncated"] === "true";
      const changed = h["x-export-changed"] === "true";
      const was = h["x-export-was-count"] === "" || h["x-export-was-count"] == null ? null : Number(h["x-export-was-count"]);

      const filename = filenameFromDisposition(h["content-disposition"], `${tenantSchema}_export.csv`);
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      // CONDITIONAL truncation line — only say "limit" when actually truncated.
      let line = truncated && total != null
        ? `Exported the first ${withCommas(returned)} of ${withCommas(total)} rows (current export limit).`
        : truncated
          ? `Exported the first ${withCommas(returned)} rows (current export limit).`
          : `Exported ${withCommas(returned)} row${returned === 1 ? "" : "s"}.`;
      if (changed && was != null) line += ` Data changed since it was shown (was ${withCommas(was)}).`;
      setMessage(line);
      setStatus("done");
    } catch (err) {
      // Calm, non-technical message; the real error is logged for the developer.
      // eslint-disable-next-line no-console
      console.error("[sq-ai] CSV export failed:", err && (err.response || err.message || err));
      setMessage("Couldn't export the data — please try again.");
      setStatus("error");
    }
  };

  return (
    <div className={styles.exportPanel}>
      {columns.length > 0 && (
        <div className={styles.exportTableWrap}>
          <table className={styles.exportTable}>
            <thead>
              <tr>{columns.map((c) => <th key={c.key}>{c.header}</th>)}</tr>
            </thead>
            <tbody>
              {sample.map((row, i) => (
                <tr key={i}>{columns.map((c) => (
                  <td key={c.key}>{formatResultDate(c.dateKind, row[c.key], { zone }) || cellText(row[c.key])}</td>
                ))}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className={styles.exportBar}>
        {!readOnly && (
          <button type="button" className={styles.downloadBtn} onClick={onDownload} disabled={!canDownload}>
            {status === "loading"
              ? <><Loader2 className={`w-3.5 h-3.5 ${styles.spin}`} strokeWidth={2} /><span>Preparing…</span></>
              : <><Download className="w-3.5 h-3.5" strokeWidth={2} /><span>Download CSV</span></>}
          </button>
        )}
        {fullRows != null && (
          <span className={styles.exportHint}>
            {sample.length < fullRows
              ? `Showing ${withCommas(sample.length)} of ${withCommas(fullRows)}`
              : `${withCommas(fullRows)} row${fullRows === 1 ? "" : "s"}`}
          </span>
        )}
      </div>
      {!readOnly && message && (
        <div className={status === "error" ? styles.exportError : styles.exportStatus}>{message}</div>
      )}
    </div>
  );
}
